import { authApi } from '@/shared/api/authApi'
import type { ConfirmOptions } from '@/shared/ui/useConfirmDialog'
import { refreshPasskeyCapability } from '@/shared/lib/platform/passkeySupport'

const PASSKEY_ENROLL_DECLINED_KEY_PREFIX = 'synap-passkey-enroll-declined'

export const passkeyEnrollmentDeclinedStorageKey = (userId: string): string =>
  `${PASSKEY_ENROLL_DECLINED_KEY_PREFIX}:${userId}`

export const hasDeclinedPasskeyEnrollment = (userId: string): boolean => {
  if (typeof window === 'undefined') return true
  return localStorage.getItem(passkeyEnrollmentDeclinedStorageKey(userId)) === '1'
}

export const markPasskeyEnrollmentDeclined = (userId: string): void => {
  if (typeof window === 'undefined') return
  localStorage.setItem(passkeyEnrollmentDeclinedStorageKey(userId), '1')
}

export const buildPasskeyEnrollmentConfirmOptions = (): ConfirmOptions => ({
  title: 'Usar chave de acesso?',
  description:
    'Cadastre biometria ou PIN do dispositivo para entrar mais rápido, sem digitar senha toda vez. Você ainda pode entrar com senha nas próximas vezes.',
  confirmLabel: 'Cadastrar agora',
  cancelLabel: 'Agora não',
  tone: 'default',
})

export type OfferPasskeyEnrollmentParams = {
  userId: string
  confirm: (options: ConfirmOptions) => Promise<boolean>
  onRegistered?: () => void
  onDeclined?: () => void
}

/**
 * Após login com senha, oferece cadastro de chave de acesso uma vez por conta
 * (até o usuário recusar explicitamente).
 */
export const offerPasskeyEnrollmentAfterLogin = async ({
  userId,
  confirm,
  onRegistered,
  onDeclined,
}: OfferPasskeyEnrollmentParams): Promise<void> => {
  if (hasDeclinedPasskeyEnrollment(userId)) return

  const capable = await refreshPasskeyCapability()
  if (!capable) return

  let passkeys: Awaited<ReturnType<typeof authApi.listPasskeys>> = []
  try {
    passkeys = await authApi.listPasskeys()
  } catch {
    return
  }

  if (passkeys.length > 0) return

  const accepted = await confirm(buildPasskeyEnrollmentConfirmOptions())
  // Perguntamos no máximo 1x na vida do usuário: assim que o convite é exibido, marcamos como
  // ofertado e nunca mais reaparece — independentemente do desfecho (aceitar, recusar, dispensar
  // ou falha silenciosa no cadastro). Depois disso, o usuário configura em Configurações.
  markPasskeyEnrollmentDeclined(userId)

  if (accepted !== true) {
    onDeclined?.()
    return
  }

  try {
    await authApi.registerPasskey()
    onRegistered?.()
  } catch {
    /* falha silenciosa — usuário pode cadastrar depois em Configurações */
  }
}
